import React, { useState, useEffect, useCallback } from 'react';
import { gatePassService, productService, warehouseService, customerService, batchService } from '../../services/financeService';
import { buildGatePassRows } from '../../utils/exportUtils';
import GatePassFormFields, { emptyForm, buildGatePassPayload, validateGatePassItems, materialLabel } from './GatePassFormFields';
import GatePassDetailModal, { STATUS_STYLE, TYPE_STYLE, badge, InlineAlert } from './GatePassDetailModal';
import CsvActions from '../common/CsvActions';
import './Financial.css';

const GatePassManagement = () => {
  const [gatePasses, setGatePasses]     = useState([]);
  const [loading, setLoading]           = useState(true);
  const [pageError, setPageError]       = useState(null);
  const [activeTab, setActiveTab]       = useState('ALL');

  const [showCreate, setShowCreate]     = useState(false);
  const [detailId, setDetailId]     = useState(null);

  const [form, setForm]                 = useState(emptyForm());
  const [formError, setFormError]       = useState(null);
  const [formLoading, setFormLoading]   = useState(false);

  const [actionLoading, setActionLoading] = useState(null);
  const [cancelId, setCancelId]           = useState(null);
  const [cancelReason, setCancelReason]   = useState('');
  const [dispatchConfirmId, setDispatchConfirmId] = useState(null);
  const [toast, setToast]                 = useState(null);
  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate]         = useState('');
  const [toDate, setToDate]             = useState('');

  const [productCatalog, setProductCatalog] = useState([]);
  const [warehouseList, setWarehouseList] = useState([]);
  const [partyList, setPartyList] = useState([]);
  const [batchList, setBatchList] = useState([]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setPageError(null);
    try {
      let res;
      if (fromDate && toDate)        res = await gatePassService.getByDateRange(fromDate, toDate);
      else if (activeTab !== 'ALL')  res = await gatePassService.getByType(activeTab);
      else if (statusFilter)         res = await gatePassService.getByStatus(statusFilter);
      else                           res = await gatePassService.getAll();

      if (res.success) setGatePasses(res.data?.content || res.data || []);
      else setPageError(res.message || 'Failed to load gate passes');
    } catch (e) {
      setPageError(e.message);
    } finally {
      setLoading(false);
    }
  }, [activeTab, statusFilter, fromDate, toDate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => {
    setForm(emptyForm());
    setFormError(null);
    setShowCreate(true);
    productService.getAll({ activeOnly: true }).then(res => {
      if (res.success) setProductCatalog(res.data || []);
    }).catch(() => {});
    warehouseService.getAll(true).then(res => {
      const list = res?.data || res || [];
      setWarehouseList(Array.isArray(list) ? list : []);
    }).catch(() => {});
    customerService.getAll().then(res => {
      const list = res?.data || res || [];
      setPartyList(Array.isArray(list) ? list : []);
    }).catch(() => {});
    batchService.getAll().then(res => {
      const list = res?.data || res || [];
      setBatchList(Array.isArray(list) ? list.filter(b => b.status === 'OPEN' || b.status === 'PROCESSING') : []);
    }).catch(() => {});
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError(null);
    // Batch reference is mandatory for ALL gate passes
    if (!form.batchId) {
      setFormError(
        form.type === 'EXIT'
          ? 'Batch reference is required. Select the batch these goods are dispatching from.'
          : 'Batch reference is required. Create the batch first, then come back to create the gate pass.'
      );
      return;
    }
    const itemError = validateGatePassItems(form);
    if (itemError) { setFormError(itemError); return; }
    setFormLoading(true);
    try {
      const payload = buildGatePassPayload(form);
      const res = await gatePassService.create(payload);
      if (res.success) { setShowCreate(false); fetchAll(); }
      else setFormError(res.message || 'Failed to create gate pass');
    } catch (e) {
      setFormError(e.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleViewDetail = (id) => setDetailId(id);

  const handleIssue = async (id) => {
    setActionLoading(id);
    try {
      const res = await gatePassService.issue(id);
      if (res.success) fetchAll();
      else setPageError(res.message || 'Failed to issue gate pass');
    } catch (e) { setPageError(e.message); }
    finally { setActionLoading(null); }
  };

  const handleReceive = async (id) => {
    setActionLoading(id);
    try {
      const res = await gatePassService.receive(id);
      if (res.success) { fetchAll(); showToast('Gate pass received — stock updated & purchase auto-created'); }
      else setPageError(res.message || 'Failed to receive gate pass');
    } catch (e) { setPageError(e.message); }
    finally { setActionLoading(null); }
  };

  const handleDispatch = (id) => setDispatchConfirmId(id);

  const doDispatch = async () => {
    const id = dispatchConfirmId;
    setDispatchConfirmId(null);
    setActionLoading(id);
    try {
      const res = await gatePassService.dispatch(id);
      if (res.success) { fetchAll(); showToast('Gate pass dispatched — stock updated'); }
      else setPageError(res.message || 'Failed to dispatch gate pass');
    } catch (e) { setPageError(e.message); }
    finally { setActionLoading(null); }
  };

  const handleCancelSubmit = async () => {
    if (!cancelReason.trim()) { setPageError('Please enter a cancellation reason'); return; }
    setActionLoading(cancelId);
    try {
      const res = await gatePassService.cancel(cancelId, cancelReason);
      if (res.success) { fetchAll(); setCancelId(null); setCancelReason(''); }
      else setPageError(res.message || 'Failed to cancel');
    } catch (e) { setPageError(e.message); }
    finally { setActionLoading(null); }
  };


  const clearFilters = () => { setFromDate(''); setToDate(''); setStatusFilter(''); setSearch(''); };
  const hasFilters = fromDate || toDate || statusFilter || search;

  const displayed = gatePasses.filter(gp =>
    !search ||
    gp.gatePassNumber?.toLowerCase().includes(search.toLowerCase()) ||
    gp.partyName?.toLowerCase().includes(search.toLowerCase()) ||
    (gp.vehicleNumber || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="financial-container">

      {/* ── Header ── */}
      <div className="financial-header">
        <div>
          <h2 className="financial-title">Gate Pass Management</h2>
          <p className="financial-subtitle">Track all material entry & exit at warehouses</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {(() => { const { headers, matrix } = buildGatePassRows(gatePasses); return (
            <CsvActions filename="gate_passes" headers={headers} matrix={matrix} />
          ); })()}
          <button className="btn-primary" onClick={openCreate}>+ New Gate Pass</button>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="tab-container">
        {[
          { key: 'ALL',   label: 'All Gate Passes' },
          { key: 'ENTRY', label: 'Entry' },
          { key: 'EXIT',  label: 'Exit' },
        ].map(t => (
          <button
            key={t.key}
            className={`tab-button ${activeTab === t.key ? 'active' : ''}`}
            onClick={() => { setActiveTab(t.key); setStatusFilter(''); setFromDate(''); setToDate(''); }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Filters ── */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px', alignItems: 'center' }}>
        <input
          className="form-control"
          style={{ maxWidth: '210px' }}
          placeholder="Search by GP#, party, vehicle..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          className="form-control"
          style={{ maxWidth: '160px' }}
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setActiveTab('ALL'); setFromDate(''); setToDate(''); }}
        >
          <option value="">All Statuses</option>
          {/* Exit gate passes are never "received" - they go Issued -> Dispatched */}
          {['DRAFT', 'ISSUED', 'RECEIVED', 'DISPATCHED', 'CANCELLED']
            .filter(s => !(activeTab === 'EXIT' && s === 'RECEIVED'))
            .map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <input type="date" className="form-control" style={{ maxWidth: '145px' }} value={fromDate} onChange={e => setFromDate(e.target.value)} />
        <input type="date" className="form-control" style={{ maxWidth: '145px' }} value={toDate}   onChange={e => setToDate(e.target.value)} />
        {hasFilters && (
          <button className="btn-secondary" style={{ fontSize: '13px', padding: '8px 14px' }} onClick={clearFilters}>
            ✕ Clear
          </button>
        )}
      </div>

      {pageError && <InlineAlert msg={pageError} />}

      {/* ── Table ── */}
      {loading ? (
        <div className="loading-state">Loading gate passes…</div>
      ) : displayed.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">—</div>
          <h3>No gate passes found</h3>
          <p>Click "New Gate Pass" to create the first one.</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Gate Pass #</th>
                <th>Type</th>
                <th>Material</th>
                <th>Date</th>
                <th>Party</th>
                <th>Warehouse</th>
                <th>Vehicle / Container</th>
                <th>Qty · Weight</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map(gp => (
                <tr key={gp.id}>
                  <td>
                    <span
                      style={{ fontWeight: 600, color: 'var(--color-primary)', cursor: 'pointer' }}
                      onClick={() => handleViewDetail(gp.id)}
                      title="View details"
                    >
                      {gp.gatePassNumber}
                    </span>
                  </td>
                  <td>{badge(gp.type, TYPE_STYLE)}</td>
                  <td style={{ fontSize: '12px' }}>{materialLabel(gp.materialType)}</td>
                  <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{gp.gatePassDate}</td>
                  <td style={{ minWidth: 160 }}>
                    <div style={{ fontWeight: 500, fontSize: '13px' }}>{gp.partyName}</div>
                    <div style={{ fontSize: '11px', color: 'var(--color-text-tertiary)' }}>{gp.partyType}</div>
                  </td>
                  <td style={{ fontSize: '12px' }}>{gp.warehouseName || '—'}</td>
                  <td style={{ fontSize: '12px' }}>
                    <div>{gp.vehicleNumber || '—'}</div>
                    {gp.containerNumber && <div style={{ color: 'var(--color-accent-hover)', fontSize: '11px' }}>{gp.containerNumber}</div>}
                  </td>
                  <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                    {gp.totalQuantity != null ? `${gp.totalQuantity}` : '—'}
                    {gp.totalWeight  != null ? ` · ${gp.totalWeight} kg` : ''}
                  </td>
                  <td>{badge(gp.status, STATUS_STYLE)}</td>
                  <td>
                    <div className="table-actions" style={{ gap: '5px' }}>
                      <button className="btn-icon" onClick={() => handleViewDetail(gp.id)} title="View details">View</button>

                      {gp.status === 'DRAFT' && (
                        <button
                          className="btn-icon"
                          style={{ background: 'var(--color-info-light)', color: 'var(--color-info)', border: '1px solid var(--color-info)' }}
                          disabled={actionLoading === gp.id}
                          onClick={() => handleIssue(gp.id)}
                        >
                          {actionLoading === gp.id ? 'Issuing...' : 'Issue'}
                        </button>
                      )}

                      {gp.status === 'ISSUED' && gp.type === 'ENTRY' && (
                        <button
                          className="btn-icon"
                          style={{ background: 'var(--color-success-light)', color: 'var(--color-success)', border: '1px solid var(--color-success-border)' }}
                          disabled={actionLoading === gp.id}
                          onClick={() => handleReceive(gp.id)}
                          title="Mark as received — auto-updates product stock"
                        >
                          {actionLoading === gp.id ? '...' : 'Receive'}
                        </button>
                      )}

                      {gp.status === 'ISSUED' && gp.type === 'EXIT' && (
                        <span
                          style={{ fontSize: '11px', color: 'var(--color-text-tertiary)', fontStyle: 'italic' }}
                          title="EXIT gate passes now dispatch automatically when you create an Invoice against them — see Invoices → New Invoice"
                        >
                          Via invoice
                        </span>
                      )}

                      {gp.status !== 'CANCELLED' && gp.status !== 'RECEIVED' && gp.status !== 'DISPATCHED' && (
                        <button
                          className="btn-icon"
                          style={{ background: 'var(--color-error-light)', color: 'var(--color-error)', border: '1px solid var(--color-error-border)' }}
                          onClick={() => { setCancelId(gp.id); setCancelReason(''); }}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Cancel Reason Modal ── */}
      {cancelId && (
        <div className="modal-overlay" onClick={() => setCancelId(null)}>
          <div className="modal-content" style={{ maxWidth: '420px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Cancel Gate Pass</h3>
              <button className="modal-close" onClick={() => setCancelId(null)}>✕</button>
            </div>
            <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', marginBottom: '14px' }}>
              Please provide a reason for cancellation. This cannot be undone.
            </p>
            <div className="form-group">
              <label className="form-label">Reason *</label>
              <textarea
                className="form-control"
                rows={3}
                placeholder="e.g. Wrong vehicle, wrong material, duplicate entry..."
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
              />
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setCancelId(null)}>Back</button>
              <button
                className="btn-danger"
                disabled={actionLoading === cancelId}
                onClick={handleCancelSubmit}
              >
                {actionLoading === cancelId ? 'Cancelling…' : 'Confirm Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Dispatch Confirm Modal ── */}
      {dispatchConfirmId && (
        <div className="modal-overlay" onClick={() => setDispatchConfirmId(null)}>
          <div className="modal-content" style={{ maxWidth: '440px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirm Dispatch</h3>
              <button className="modal-close" onClick={() => setDispatchConfirmId(null)}>✕</button>
            </div>
            <div style={{ padding: '4px 0 16px', fontSize: '14px', color: 'var(--color-neutral-700)', lineHeight: 1.6 }}>
              <p style={{ marginBottom: '10px' }}>Dispatch will <strong>deduct stock</strong> for all linked products.</p>
              <div style={{ background: 'var(--color-warning-light)', border: '1px solid var(--color-warning-border)', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: 'var(--color-warning-strong)' }}>
                <strong>Warning:</strong> If you already raised an invoice for these goods and stock was deducted there, do <strong>not</strong> dispatch — it will double-deduct.
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setDispatchConfirmId(null)}>Go back</button>
              <button className="btn-primary" onClick={doDispatch}>Yes, dispatch</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Create Gate Pass Modal ── */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => !formLoading && setShowCreate(false)}>
          <div className="modal-content" style={{ maxWidth: '780px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>New Gate Pass</h3>
              <button className="modal-close" onClick={() => !formLoading && setShowCreate(false)}>✕</button>
            </div>

            <form onSubmit={handleCreate}>
              <InlineAlert msg={formError} />

              <GatePassFormFields
                form={form} setForm={setForm}
                productCatalog={productCatalog} setProductCatalog={setProductCatalog}
                warehouseList={warehouseList} partyList={partyList} batchList={batchList}
              />

              <div className="modal-footer">
                <button type="button" className="btn-secondary" disabled={formLoading} onClick={() => setShowCreate(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={formLoading}>
                  {formLoading ? 'Creating…' : 'Create Gate Pass'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Detail Modal (shared with the batch screens) ── */}
      {detailId !== null && (
        <GatePassDetailModal gatePassId={detailId} onClose={() => setDetailId(null)} onChanged={fetchAll} />
      )}
      {/* ── Toast Notification ── */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999,
          background: toast.type === 'error' ? 'var(--color-error-light)' : 'var(--color-success-light)',
          color: toast.type === 'error' ? 'var(--color-error)' : 'var(--color-success)',
          border: `1px solid ${toast.type === 'error' ? 'var(--color-error-border)' : 'var(--color-success-border)'}`,
          padding: '14px 22px', borderRadius: '10px', fontSize: '14px', fontWeight: 600,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          display: 'flex', alignItems: 'center', gap: '10px',
        }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default GatePassManagement;
